import { FC, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Box, FormInputText, Link } from '@hopskipdrive/react-components';
import { ArrowBack, Email, ErrorOutlineOutlined, LockRounded } from '@mui/icons-material';
import { CircularProgress, InputAdornment } from '@mui/material';
import { HandleLogin } from '@rideiq/auth/types';
import { Button } from '@rideiq/shared/components/Button';
import { Text } from '@rideiq/shared/components/Text';
import { colorTokens } from '@rideiq/shared/components/variables';
import { FormA11yErrorAnnouncer } from '@rideiq/shared/features/FormA11yErrorAnnouncer';
import * as yup from 'yup';

import { discoverSso, initiateSsoLogin, SsoDiscoverResponse } from '../../../../api/services/Sso';
import { ContainerForBanner, StyledAction } from '../LoginForm/styles';

import { BackToLoginRow, ConfirmedEmailInput, EmailLabel, OrDivider, SsoProviderButton, SsoSubtitle } from './styles';

const emailSchema = yup.object().shape({
  email: yup.string().email().required(),
});

const passwordSchema = yup.object().shape({
  password: yup.string().min(8).max(32).required(),
});

export interface SSOLoginFormProps {
  handleSignIn: HandleLogin;
  handleForgotPassword: () => void;
  serverErrors?: string[];
}

type Step = 'email' | 'options';

export const SSOLoginForm: FC<SSOLoginFormProps> = ({ handleSignIn, handleForgotPassword, serverErrors }) => {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [discoverResult, setDiscoverResult] = useState<SsoDiscoverResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [discoverErrors, setDiscoverErrors] = useState<string[]>([]);

  const handleEmailNext = async (submittedEmail: string) => {
    setIsLoading(true);
    setDiscoverErrors([]);
    const response = await discoverSso(submittedEmail);

    setIsLoading(false);

    if (response.type === 'success') {
      setEmail(submittedEmail);
      setDiscoverResult(response.data);
      setStep('options');
    } else {
      setEmail(submittedEmail);
      setDiscoverErrors(response.error.errors);
    }
  };

  const handleChangeEmail = () => {
    setStep('email');
    setDiscoverResult(null);
    setDiscoverErrors([]);
  };

  if (step === 'email') {
    return <EmailStep onNext={handleEmailNext} isLoading={isLoading} errors={discoverErrors} />;
  }

  return (
    <OptionsStep
      email={email}
      discoverResult={discoverResult!}
      onChangeEmail={handleChangeEmail}
      handleSignIn={handleSignIn}
      handleForgotPassword={handleForgotPassword}
      serverErrors={serverErrors}
    />
  );
};

// --- Shared ---

const ErrorBulletList: FC<{ errors: string[] }> = ({ errors }) => (
  <Box
    component="ul"
    m={0}
    mt={0.5}
    pl={0}
    sx={{
      color: colorTokens.error.main,
      listStyle: 'none',
      '& li': {
        paddingLeft: '1.25em',
        position: 'relative',
        '&::before': { content: '"•"', position: 'absolute', left: 0 },
      },
    }}
  >
    {errors.map((error) => (
      <Box component="li" key={error}>
        {error}
      </Box>
    ))}
  </Box>
);

// --- Email Step ---

interface EmailStepProps {
  onNext: (email: string) => void;
  isLoading: boolean;
  errors: string[];
}

const EmailStep: FC<EmailStepProps> = ({ onNext, isLoading, errors }) => {
  const {
    control,
    handleSubmit,
    formState: { errors: formErrors },
  } = useForm<{ email: string }>({
    resolver: yupResolver(emailSchema),
    shouldUnregister: true,
  });

  const submit = ({ email }, e) => {
    e.preventDefault();
    onNext(email);
  };

  const hasErrors = errors.length > 0;

  return (
    <ContainerForBanner>
      <Box role="alert" aria-live="polite" mb={hasErrors ? 2 : 0}>
        {hasErrors && (
          <Alert
            severity="error"
            message={
              <Box display="flex" flexDirection="column" alignItems="start" gap={0.5} m={0} p={0}>
                <Text textColor={colorTokens.error.main}>
                  <strong>Authentication failed.</strong> This could be due to:
                </Text>
                <ErrorBulletList errors={errors} />
              </Box>
            }
          />
        )}
      </Box>
      <Text variant="h1">Sign in</Text>
      <form className="form" onSubmit={handleSubmit(submit)}>
        <FormA11yErrorAnnouncer errors={formErrors} />
        <Controller
          name="email"
          control={control}
          render={({ field: { value, onChange } }) => (
            <FormInputText
              aria-label="Email Address"
              id="sso-login-email"
              inputProps={{ 'data-testid': 'sso-email', autocomplete: 'email' }}
              placeholder="Email Address"
              type="email"
              variant="outlined"
              icon={<Email />}
              error={!!formErrors.email || hasErrors}
              autoFocus
              helperText={formErrors.email ? formErrors.email.message : ''}
              value={value}
              onChange={onChange}
            />
          )}
        />
        <Box mt={1}>
          <Button type="submit" color="primary" data-testid="ssoNextButton" loading={isLoading} fullWidth>
            {hasErrors ? 'Try Again' : 'Next'}
          </Button>
        </Box>
      </form>
    </ContainerForBanner>
  );
};

// --- Options Step ---

interface OptionsStepProps {
  email: string;
  discoverResult: SsoDiscoverResponse;
  onChangeEmail: () => void;
  handleSignIn: HandleLogin;
  handleForgotPassword: () => void;
  serverErrors?: string[];
}

const useSsoLogin = (email: string) => {
  const [ssoErrors, setSsoErrors] = useState<string[]>([]);
  const [ssoLoading, setSsoLoading] = useState(false);

  const handleSsoLogin = async (uuid: string) => {
    setSsoLoading(true);
    setSsoErrors([]);
    const response = await initiateSsoLogin(uuid, email);

    setSsoLoading(false);

    if (response.type === 'success') {
      window.location.href = response.data.loginUrl;
    } else {
      setSsoErrors(response.error.errors);
    }
  };

  return { ssoErrors, ssoLoading, hasSsoErrors: ssoErrors.length > 0, handleSsoLogin };
};

const SsoFailureSection: FC<{ errors: string[] }> = ({ errors }) => (
  <Box role="alert" aria-live="polite" mb={2}>
    <Box display="flex" flexDirection="column" alignItems="center" mb={1}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{ width: 56, height: 56, borderRadius: '50%', backgroundColor: '#fde8e8', mb: 1.5 }}
      >
        <ErrorOutlineOutlined sx={{ color: colorTokens.error.main, fontSize: 28 }} />
      </Box>
      <Text variant="h1" sx={{ margin: 0 }}>
        Sign in failed
      </Text>
    </Box>
    <SsoSubtitle variant="body2" sx={{ mb: 2, textAlign: 'center' }}>
      We couldn&apos;t complete your SSO authentication
    </SsoSubtitle>
    <Alert
      severity="error"
      message={
        <Box m={0} p={0} textAlign="left">
          <Text textColor={colorTokens.error.main}>
            <strong>Authentication failed.</strong> This could be due to:
          </Text>
          <ErrorBulletList errors={errors} />
        </Box>
      }
    />
  </Box>
);

const OptionsStep: FC<OptionsStepProps> = ({
  email,
  discoverResult,
  onChangeEmail,
  handleSignIn,
  handleForgotPassword,
  serverErrors,
}) => {
  const { ssoEnabled, ssoRequired, configs } = discoverResult;
  const showSsoButtons = ssoEnabled && configs.length > 0;
  const showPasswordField = !ssoEnabled || !ssoRequired;
  const isSsoOnly = ssoEnabled && ssoRequired;

  const { ssoErrors, ssoLoading, hasSsoErrors, handleSsoLogin } = useSsoLogin(email);

  const {
    control,
    handleSubmit,
    resetField,
    formState: { errors },
  } = useForm<{ password: string }>({
    resolver: yupResolver(passwordSchema),
    shouldUnregister: true,
    defaultValues: { password: '' },
  });

  const submit = ({ password }, e) => {
    e.preventDefault();
    handleSignIn({ email, password }, (name) => {
      if (name === 'password') {
        resetField('password');
      }
    });
  };

  return (
    <ContainerForBanner>
      {!hasSsoErrors && (serverErrors ?? []).length > 0 && (
        <Box role="alert" aria-live="polite" display="flex" flexDirection="column" gap={1} mb={2}>
          {serverErrors!.map((error) => (
            <Alert key={error} severity="error" message={error} icon={false} />
          ))}
        </Box>
      )}

      <form className="form" onSubmit={handleSubmit(submit)}>
        <FormA11yErrorAnnouncer errors={errors} />

        {hasSsoErrors ? (
          <SsoFailureSection errors={ssoErrors} />
        ) : (
          <Text variant="h1" sx={{ marginBottom: '10px' }}>
            {isSsoOnly ? 'Sign in with SSO' : 'Sign in'}
          </Text>
        )}

        {isSsoOnly && !hasSsoErrors && <SsoSubtitle variant="body2">Your organization uses single sign-on</SsoSubtitle>}

        <EmailLabel variant="caption">Email</EmailLabel>
        <ConfirmedEmailInput
          aria-label="Email Address"
          id="sso-confirmed-email"
          inputProps={{ 'data-testid': 'sso-confirmed-email' }}
          variant="outlined"
          disabled
          value={email}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockRounded fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Link component="button" type="button" onClick={onChangeEmail} data-testid="sso-change-email">
                  Change
                </Link>
              </InputAdornment>
            ),
          }}
        />

        {showSsoButtons &&
          configs.map((config) => (
            <SsoProviderButton
              key={config.uuid}
              type="button"
              data-testid={`sso-provider-${config.provider}`}
              disabled={ssoLoading}
              onClick={() => handleSsoLogin(config.uuid)}
            >
              {ssoLoading ? <CircularProgress size={16} /> : getProviderIcon(config.provider)}
              {hasSsoErrors ? 'Try again' : `Sign in with ${formatProviderName(config.provider)}`}
            </SsoProviderButton>
          ))}

        {showSsoButtons && showPasswordField && !hasSsoErrors && <OrDivider>OR</OrDivider>}

        {showPasswordField && !hasSsoErrors && (
          <Controller
            name="password"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormInputText
                aria-label="Password"
                id="sso-login-password"
                inputProps={{ 'data-testid': 'password', autocomplete: 'current-password' }}
                placeholder="Password"
                type="password"
                variant="outlined"
                icon={<LockRounded />}
                error={!!errors.password || !!serverErrors?.length}
                helperText={errors.password ? errors.password.message : ''}
                value={value}
                onChange={onChange}
              />
            )}
          />
        )}

        {showPasswordField && !hasSsoErrors && (
          <StyledAction className="form-actions">
            <Button type="submit" color="primary" data-testid="submitButton">
              Sign In
            </Button>
            <Link component="button" onClick={handleForgotPassword}>
              Forgot password
            </Link>
          </StyledAction>
        )}

        <BackToLoginRow>
          <Link component="button" type="button" onClick={onChangeEmail} data-testid="sso-back-to-login">
            <Box display="flex" gap="1rem">
              <ArrowBack fontSize="small" />
              <Text>Back to login</Text>
            </Box>
          </Link>
        </BackToLoginRow>

        {hasSsoErrors && (
          <Box mt={2} textAlign="center">
            <Text variant="caption" color="text.secondary">
              Need help with SSO access? Contact your IT administrator or{' '}
              <Link href="mailto:help@hopskipdrive.com">help@hopskipdrive.com</Link>
            </Text>
          </Box>
        )}
      </form>
    </ContainerForBanner>
  );
};

function formatProviderName(provider: string): string {
  return provider.charAt(0).toUpperCase() + provider.slice(1);
}

function getProviderIcon(provider: string): JSX.Element | null {
  if (provider === 'microsoft') {
    return (
      <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <rect x="1" y="1" width="8.5" height="8.5" fill="#F25022" />
        <rect x="10.5" y="1" width="8.5" height="8.5" fill="#7FBA00" />
        <rect x="1" y="10.5" width="8.5" height="8.5" fill="#00A4EF" />
        <rect x="10.5" y="10.5" width="8.5" height="8.5" fill="#FFB900" />
      </svg>
    );
  }

  return null;
}
